import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, Copy } from "lucide-react";
import {
  DataTable,
  PageTitle,
  MasterCrudPage,
  useRouteModulePermissions,
} from "@/components/common/Crud";
import { MultiCheck } from "@/components/common/MultiCheck";
import { Combobox } from "@/components/common/Combobox";
import { DatePicker } from "@/components/common/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function SubNav({ items }) {
  return (
    <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1 text-xs">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          data-testid={`acad-${i.to.split("/").pop()}`}
          className={({ isActive }) =>
            `px-3 py-1.5 border-b-2 -mb-px ${isActive ? "border-blue-600 text-blue-600 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`
          }
        >
          {i.label}
        </NavLink>
      ))}
    </div>
  );
}

const TT_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function TimetablePlannerPage() {
  const permissions = useRouteModulePermissions("academic", "timetables");
  const { branchId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [periodMasters, setPeriodMasters] = useState([]);
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [subjectGroupId, setSubjectGroupId] = useState("");
  const [planDate, setPlanDate] = useState("");
  const [dateTouched, setDateTouched] = useState(false);
  const [copyFromDate, setCopyFromDate] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    period_master_id: "",
    day_of_week: "Monday",
    period_no: "",
    subject_id: "",
    teacher_id: "",
    start_time: "",
    end_time: "",
  });

  const loadMasters = useCallback(async () => {
    try {
      const [c, s, sg, pm, st] = await Promise.all([
        api.get("/classes", { params: { branch_id: branchId } }),
        api.get("/subjects", { params: { branch_id: branchId } }),
        api.get("/subject-groups", { params: { branch_id: branchId } }),
        api.get("/period-master", {
          params: { branch_id: branchId, limit: 1000 },
        }),
        api.get("/staff", { params: { branch_id: branchId, limit: 2000 } }),
      ]);
      setClasses(c.data.items || []);
      setSubjects(s.data.items || []);
      setSubjectGroups(sg.data.items || []);
      setPeriodMasters(pm.data.items || []);
      setStaff(st.data.items || []);
    } catch {
      toast.error("Failed to load timetable masters");
    }
  }, [branchId]);

  const loadSections = useCallback(async () => {
    if (!classId) {
      setSections([]);
      setSectionId("");
      return;
    }
    try {
      const { data } = await api.get("/sections", {
        params: { class_id: classId },
      });
      setSections(data.items || []);
      if (
        sectionId &&
        !(data.items || []).some((s) => String(s.id) === String(sectionId))
      ) {
        setSectionId("");
      }
    } catch {
      toast.error("Failed to load sections");
    }
  }, [classId, sectionId]);

  const loadRows = useCallback(async () => {
    if (!classId || !sectionId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/timetables", {
        params: { class_id: classId, section_id: sectionId, limit: 1000 },
      });
      setRows(data.items || []);
    } catch {
      toast.error("Failed to load timetable");
    }
    setLoading(false);
  }, [classId, sectionId]);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!classId || !sectionId) {
      return;
    }

    if (dateTouched && planDate) {
      return;
    }

    const dateList = Array.from(
      new Set(
        rows
          .map((r) => String(r.plan_date || "").slice(0, 10))
          .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)),
      ),
    ).sort();

    if (dateList.length > 0) {
      setPlanDate(dateList[dateList.length - 1]);
    }
  }, [rows, classId, sectionId, planDate, dateTouched]);

  const selectedClass = classes.find((c) => String(c.id) === String(classId));
  const selectedSection = sections.find(
    (s) => String(s.id) === String(sectionId),
  );
  const selectedGroup = subjectGroups.find(
    (g) => String(g.id) === String(subjectGroupId),
  );

  const filteredTeachers = staff.filter((member) => {
    const role = String(member.role || "").toLowerCase();
    const teacherType = String(member.teacher_type || "").toLowerCase();
    return role === "teacher" || teacherType.includes("teach");
  });

  const teacherOptions = filteredTeachers
    .map((t) => ({
      value: String(t.id),
      label: `${t.first_name || ""} ${t.last_name || ""}`.trim() || `#${t.id}`,
      sub: `${t.staff_id || t.staff_no || ""}${t.teacher_type ? ` | ${t.teacher_type}` : ""}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const subjectIdsFromGroup = (selectedGroup?.subjects || []).map((v) =>
    String(v),
  );
  const filteredSubjects = subjects.filter((s) => {
    if (!subjectGroupId) return false;
    return subjectIdsFromGroup.includes(String(s.id));
  });

  const subjectOptions = filteredSubjects
    .map((s) => ({
      value: String(s.id),
      label: s.name,
      sub: s.code ? `Code: ${s.code}` : "",
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const dateRows = rows.filter((r) => {
    const rowDate = (r.plan_date || "").slice(0, 10);
    return rowDate === planDate;
  });

  const periodMasterOptions = periodMasters
    .filter((p) => String(p.status || "active").toLowerCase() !== "inactive")
    .sort((a, b) => {
      const sortDiff = Number(a.sort_order || 0) - Number(b.sort_order || 0);
      if (sortDiff !== 0) return sortDiff;
      return Number(a.period_no || 0) - Number(b.period_no || 0);
    });

  const periodSortKey = (row) => {
    const pm = periodMasters.find(
      (p) => Number(p.period_no || 0) === Number(row.period_no || 0),
    );
    return Number(pm?.sort_order || row.period_no || 9999);
  };

  const periodRows = (() => {
    if (periodMasterOptions.length > 0) {
      return periodMasterOptions.map((p) => ({
        period_no: Number(p.period_no || 0),
        sort_order: Number(p.sort_order || 0),
        label: p.name || `Period ${p.period_no}`,
        start_time: p.start_time || "",
        end_time: p.end_time || "",
      }));
    }

    const uniq = Array.from(
      new Set(
        dateRows
          .map((r) => Number(r.period_no || 0))
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    ).sort((a, b) => a - b);

    return uniq.map((periodNo) => ({
      period_no: periodNo,
      sort_order: periodNo,
      label: `Period ${periodNo}`,
      start_time: "",
      end_time: "",
    }));
  })();

  const rowsByDayPeriod = dateRows.reduce((acc, row) => {
    const key = `${row.day_of_week || ""}::${row.period_no || ""}`;
    acc[key] = row;
    return acc;
  }, {});

  const nextPeriodNo = (() => {
    if (periodMasterOptions.length > 0) {
      const used = new Set(dateRows.map((r) => Number(r.period_no || 0)));
      const firstFree = periodMasterOptions.find(
        (p) => !used.has(Number(p.period_no || 0)),
      );
      if (firstFree) return Number(firstFree.period_no || 1);
    }
    const maxPeriod = dateRows.reduce(
      (max, r) => Math.max(max, Number(r.period_no || 0)),
      0,
    );
    return maxPeriod + 1;
  })();

  const toPayload = (draft) => ({
    class_id: Number(classId),
    section_id: Number(sectionId),
    plan_date: planDate,
    day_of_week: draft.day_of_week,
    period_no: Number(draft.period_no),
    subject_id: draft.subject_id ? Number(draft.subject_id) : null,
    teacher_id: draft.teacher_id ? Number(draft.teacher_id) : null,
    start_time: draft.start_time || null,
    end_time: draft.end_time || null,
  });

  const openCreate = () => {
    if (!classId || !sectionId) {
      toast.error("Please select class and section first");
      return;
    }
    if (!subjectGroupId) {
      toast.error("Please select subject group first");
      return;
    }
    setEditing(null);
    const defaultMaster =
      periodMasterOptions.find(
        (p) => Number(p.period_no || 0) === nextPeriodNo,
      ) || periodMasterOptions[0];
    setForm({
      period_master_id: defaultMaster ? String(defaultMaster.id) : "",
      day_of_week: "Monday",
      period_no: String(defaultMaster?.period_no || nextPeriodNo),
      subject_id: "",
      teacher_id: "",
      start_time: (defaultMaster?.start_time || "").slice(0, 5),
      end_time: (defaultMaster?.end_time || "").slice(0, 5),
    });
    setOpen(true);
  };

  const openCreateForSlot = (day, periodNo) => {
    if (!classId || !sectionId) {
      toast.error("Please select class and section first");
      return;
    }
    if (!subjectGroupId) {
      toast.error("Please select subject group first");
      return;
    }
    const defaultMaster = periodMasterOptions.find(
      (p) => Number(p.period_no || 0) === Number(periodNo || 0),
    );
    setEditing(null);
    setForm({
      period_master_id: defaultMaster ? String(defaultMaster.id) : "",
      day_of_week: day,
      period_no: String(periodNo || defaultMaster?.period_no || nextPeriodNo),
      subject_id: "",
      teacher_id: "",
      start_time: (defaultMaster?.start_time || "").slice(0, 5),
      end_time: (defaultMaster?.end_time || "").slice(0, 5),
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    const mappedMaster = periodMasters.find(
      (p) => Number(p.period_no || 0) === Number(row.period_no || 0),
    );
    setEditing(row);
    setForm({
      period_master_id: mappedMaster ? String(mappedMaster.id) : "",
      day_of_week: row.day_of_week || "Monday",
      period_no: String(row.period_no || ""),
      subject_id: row.subject_id ? String(row.subject_id) : "",
      teacher_id: row.teacher_id ? String(row.teacher_id) : "",
      start_time: (row.start_time || "").slice(0, 5),
      end_time: (row.end_time || "").slice(0, 5),
    });
    setOpen(true);
  };

  const saveRow = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for timetable");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for timetable");
      return;
    }
    if (saving) return;
    if (!classId || !sectionId || !planDate) {
      toast.error("Class, section and date are required");
      return;
    }
    if (!subjectGroupId) {
      toast.error("Subject group is required");
      return;
    }
    if (!form.period_no || Number(form.period_no) <= 0) {
      toast.error("Period no is required");
      return;
    }
    if (!form.subject_id) {
      toast.error("Subject is required");
      return;
    }
    if (!subjectIdsFromGroup.includes(String(form.subject_id))) {
      toast.error("Please select subject only from selected subject group");
      return;
    }

    setSaving(true);
    try {
      const payload = toPayload(form);
      const slotConflict = dateRows.find(
        (r) =>
          String(r.day_of_week || "") === String(payload.day_of_week) &&
          Number(r.period_no || 0) === Number(payload.period_no) &&
          String(r.id) !== String(editing?.id || ""),
      );

      if (editing) {
        await api.put(`/timetables/${editing.id}`, payload);
        toast.success("Timetable updated");
      } else if (slotConflict?.id) {
        await api.put(`/timetables/${slotConflict.id}`, payload);
        toast.success("Existing slot revised");
      } else {
        await api.post("/timetables", payload);
        toast.success("Timetable period added");
      }

      setOpen(false);
      await loadRows();
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for timetable");
      return;
    }
    try {
      await api.delete(`/timetables/${deleteRow.id}`);
      toast.success("Period deleted");
      setDeleteRow(null);
      await loadRows();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyFromDatePlan = async () => {
    if (!classId || !sectionId || !planDate || !copyFromDate) {
      toast.error("Class, section, source date and target date required");
      return;
    }
    if (copyFromDate === planDate) {
      toast.error("Source and target dates cannot be same");
      return;
    }

    const sourceRows = rows.filter(
      (r) => (r.plan_date || "").slice(0, 10) === copyFromDate,
    );
    if (sourceRows.length === 0) {
      toast.info("No timetable found on source date");
      return;
    }

    setSaving(true);
    try {
      let revised = 0;
      let created = 0;
      const targetRows = rows.filter(
        (r) => (r.plan_date || "").slice(0, 10) === planDate,
      );

      for (const row of sourceRows) {
        const payload = {
          class_id: Number(classId),
          section_id: Number(sectionId),
          plan_date: planDate,
          day_of_week: row.day_of_week,
          period_no: Number(row.period_no || 0),
          subject_id: row.subject_id ? Number(row.subject_id) : null,
          teacher_id: row.teacher_id ? Number(row.teacher_id) : null,
          start_time: row.start_time || null,
          end_time: row.end_time || null,
        };

        const target = targetRows.find(
          (t) =>
            String(t.day_of_week || "") === String(payload.day_of_week) &&
            Number(t.period_no || 0) === Number(payload.period_no),
        );

        if (target?.id) {
          await api.put(`/timetables/${target.id}`, payload);
          revised += 1;
        } else {
          await api.post("/timetables", payload);
          created += 1;
        }
      }

      toast.success(`Copied timetable: ${created} created, ${revised} revised`);
      await loadRows();
    } catch {
      toast.error("Copy timetable failed");
    } finally {
      setSaving(false);
    }
  };

  const printClassSectionTimetable = () => {
    if (!classId || !sectionId || !planDate) {
      toast.error("Select class, section and date first");
      return;
    }
    const printableRows = rows
      .filter((r) => (r.plan_date || "").slice(0, 10) === planDate)
      .sort((a, b) => {
        const dayDiff =
          TT_DAYS.indexOf(a.day_of_week) - TT_DAYS.indexOf(b.day_of_week);
        if (dayDiff !== 0) return dayDiff;
        const sortDiff = periodSortKey(a) - periodSortKey(b);
        if (sortDiff !== 0) return sortDiff;
        return Number(a.period_no || 0) - Number(b.period_no || 0);
      });

    if (printableRows.length === 0) {
      toast.info("No timetable found for selected date");
      return;
    }

    const subjectName = (sid) =>
      subjects.find((s) => String(s.id) === String(sid))?.name || "-";
    const teacherName = (tid) => {
      const t = staff.find((s) => String(s.id) === String(tid));
      if (!t) return "-";
      return `${t.first_name || ""} ${t.last_name || ""}`.trim() || `#${tid}`;
    };

    const printPeriodRows =
      periodRows.length > 0
        ? periodRows
        : Array.from(
            new Set(
              printableRows
                .map((r) => Number(r.period_no || 0))
                .filter((n) => n > 0),
            ),
          )
            .sort((a, b) => a - b)
            .map((periodNo) => ({
              period_no: periodNo,
              sort_order: periodNo,
              label: `Period ${periodNo}`,
              start_time: "",
              end_time: "",
            }));

    const weekRowsHtml = printPeriodRows
      .map((period) => {
        const dayCells = TT_DAYS.map((day) => {
          const row = printableRows.find(
            (r) =>
              String(r.day_of_week || "") === day &&
              Number(r.period_no || 0) === Number(period.period_no || 0),
          );
          if (!row) return "<td>-</td>";
          const timing =
            row.start_time && row.end_time
              ? `${String(row.start_time).slice(0, 5)} - ${String(row.end_time).slice(0, 5)}`
              : "";
          return `<td><div>${subjectName(row.subject_id)}</div><div style=\"font-size:11px;color:#475569;\">${teacherName(row.teacher_id)}</div><div style=\"font-size:10px;color:#64748b;\">${timing}</div></td>`;
        }).join("");

        return `<tr><td>${period.label}</td>${dayCells}</tr>`;
      })
      .join("");

    const html = `<!doctype html><html><head><title>Class Timetable</title><style>
      body{font-family:Arial,sans-serif;padding:18px;color:#111827}
      h2{margin:0 0 8px 0}
      .meta{font-size:12px;color:#475569;margin-bottom:8px}
      table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:8px}
      th,td{border:1px solid #cbd5e1;padding:6px;text-align:left}
      th{background:#f8fafc}
    </style></head><body>
      <h2>Class Timetable</h2>
      <div class="meta">Class: ${selectedClass?.name || ""} | Section: ${selectedSection?.name || ""} | Date: ${planDate}</div>
      <table>
        <thead>
          <tr><th>Period</th>${TT_DAYS.map((d) => `<th>${d}</th>`).join("")}</tr>
        </thead>
        <tbody>${weekRowsHtml}</tbody>
      </table>
    </body></html>`;

    const w = window.open("", "_blank", "width=1200,height=800");
    if (!w) {
      toast.error("Popup blocked. Allow popups to print.");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div data-testid="timetable-page" className="space-y-3">
      <PageTitle
        title="Timetable"
        subtitle="Class-section wise day timetable with period no, date revision and print"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={printClassSectionTimetable}
              data-testid="tt-print"
            >
              <Printer className="w-3.5 h-3.5 mr-1" /> Print
            </Button>
            <Button
              size="sm"
              className="h-9 erp-primary"
              onClick={openCreate}
              data-testid="tt-add"
              disabled={!permissions.canAdd}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Period
            </Button>
          </>
        }
      />

      <div className="border rounded-md border-slate-200 bg-white p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs font-semibold">Class *</Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={classId}
              onChange={(e) => {
                setClassId(e.target.value);
                setSectionId("");
                setSubjectGroupId("");
                setPlanDate("");
                setDateTouched(false);
              }}
              data-testid="tt-class"
            >
              <option value="">Select class</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Section *</Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={sectionId}
              onChange={(e) => {
                setSectionId(e.target.value);
                setPlanDate("");
                setDateTouched(false);
              }}
              data-testid="tt-section"
              disabled={!classId}
            >
              <option value="">Select section</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Subject Group</Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={subjectGroupId}
              onChange={(e) => setSubjectGroupId(e.target.value)}
              data-testid="tt-group"
            >
              <option value="">Select subject group</option>
              {subjectGroups
                .filter(
                  (g) => !classId || String(g.class_id) === String(classId),
                )
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </select>
            {!subjectGroupId && (
              <div className="text-[10px] text-amber-600 mt-1">
                Timetable assignment ke liye subject group mandatory hai.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold">Date *</Label>
            <DatePicker
              value={planDate}
              onChange={(value) => {
                setPlanDate(value);
                setDateTouched(true);
              }}
              className="h-9 w-full"
              testId="tt-date"
            />
            {!planDate && (
              <div className="text-[10px] text-slate-500 mt-1">
                First-time entry ke liye date select karein. Existing timetable
                ho to latest date auto-load ho jayega.
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs font-semibold">Copy From Date</Label>
              <DatePicker
                value={copyFromDate}
                onChange={setCopyFromDate}
                className="h-9 w-44"
                testId="tt-copy-date"
              />
            </div>
            <Button
              variant="outline"
              className="h-9"
              onClick={copyFromDatePlan}
              disabled={saving || !permissions.canAdd}
              data-testid="tt-copy"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy Timetable
            </Button>
          </div>
          <div className="text-xs text-slate-500 md:text-right">
            Day-wise dynamic weekly editor enabled
          </div>
        </div>
      </div>

      <div className="border rounded-md border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-slate-50 text-slate-700 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left border-b border-slate-200">
                  Period
                </th>
                {TT_DAYS.map((day) => (
                  <th
                    key={day}
                    className="px-3 py-2 text-left border-b border-slate-200"
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!classId || !sectionId ? (
                <tr>
                  <td
                    colSpan={TT_DAYS.length + 1}
                    className="px-3 py-10 text-center text-slate-400 text-xs"
                  >
                    Select class and section to manage timetable
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td
                    colSpan={TT_DAYS.length + 1}
                    className="px-3 py-10 text-center text-slate-400 text-xs"
                  >
                    Loading timetable...
                  </td>
                </tr>
              ) : !planDate ? (
                <tr>
                  <td
                    colSpan={TT_DAYS.length + 1}
                    className="px-3 py-10 text-center text-slate-400 text-xs"
                  >
                    Select date to start timetable entry
                  </td>
                </tr>
              ) : periodRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={TT_DAYS.length + 1}
                    className="px-3 py-10 text-center text-slate-400 text-xs"
                  >
                    Add period templates in Period Master for faster day-wise
                    planning
                  </td>
                </tr>
              ) : (
                periodRows.map((period) => (
                  <tr
                    key={`period-${period.period_no}`}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium">{period.label}</div>
                      <div className="text-[10px] text-slate-500">
                        No. {period.period_no}
                      </div>
                    </td>
                    {TT_DAYS.map((day) => {
                      const key = `${day}::${period.period_no}`;
                      const cell = rowsByDayPeriod[key];
                      const subject = cell
                        ? subjects.find(
                            (s) => String(s.id) === String(cell.subject_id),
                          )?.name || "-"
                        : "";
                      const teacher = cell
                        ? (() => {
                            const t = staff.find(
                              (s) => String(s.id) === String(cell.teacher_id),
                            );
                            return t
                              ? `${t.first_name || ""} ${t.last_name || ""}`.trim()
                              : "-";
                          })()
                        : "";
                      const timeLabel =
                        cell && cell.start_time && cell.end_time
                          ? `${String(cell.start_time).slice(0, 5)} - ${String(cell.end_time).slice(0, 5)}`
                          : "";

                      return (
                        <td
                          key={key}
                          className="px-2 py-2 align-top border-l border-slate-100"
                        >
                          {cell ? (
                            <div className="space-y-1">
                              <div className="text-xs font-semibold text-slate-800">
                                {subject}
                              </div>
                              <div className="text-[11px] text-slate-600">
                                {teacher}
                              </div>
                              {timeLabel && (
                                <div className="text-[10px] text-slate-500">
                                  {timeLabel}
                                </div>
                              )}
                              <div className="flex gap-1 pt-1">
                                <button
                                  className="p-1 rounded hover:bg-blue-50 erp-text-primary"
                                  onClick={() => openEdit(cell)}
                                  data-testid={`tt-edit-${cell.id}`}
                                  disabled={!permissions.canEdit}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  className="p-1 rounded hover:bg-red-50 text-red-600"
                                  onClick={() => setDeleteRow(cell)}
                                  data-testid={`tt-del-${cell.id}`}
                                  disabled={!permissions.canDelete}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="w-full rounded border border-dashed border-slate-300 text-slate-500 text-xs py-2 hover:bg-slate-50"
                              onClick={() =>
                                openCreateForSlot(day, period.period_no)
                              }
                              data-testid={`tt-add-${day.toLowerCase()}-${period.period_no}`}
                              disabled={!permissions.canAdd}
                            >
                              + Add
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Revise" : "Add"} Timetable Period
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Period Master</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.period_master_id}
                onChange={(e) => {
                  const value = e.target.value;
                  const pm = periodMasterOptions.find(
                    (p) => String(p.id) === String(value),
                  );
                  setForm({
                    ...form,
                    period_master_id: value,
                    period_no: pm ? String(pm.period_no || "") : form.period_no,
                    start_time: pm
                      ? String(pm.start_time || "").slice(0, 5)
                      : form.start_time,
                    end_time: pm
                      ? String(pm.end_time || "").slice(0, 5)
                      : form.end_time,
                  });
                }}
                data-testid="tt-form-period-master"
              >
                <option value="">Custom period</option>
                {periodMasterOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || `Period ${p.period_no}`} (No. {p.period_no})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Day *</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.day_of_week}
                onChange={(e) =>
                  setForm({ ...form, day_of_week: e.target.value })
                }
                data-testid="tt-form-day"
              >
                {TT_DAYS.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Period No *</Label>
              <Input
                type="number"
                min={1}
                value={form.period_no}
                onChange={(e) =>
                  setForm({
                    ...form,
                    period_no: e.target.value,
                    period_master_id: "",
                  })
                }
                className="h-9"
                data-testid="tt-form-period"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Subject *</Label>
              <Combobox
                value={form.subject_id}
                onChange={(value) => setForm({ ...form, subject_id: value })}
                options={subjectOptions}
                placeholder="Select subject"
                testid="tt-form-subject"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">
                Teacher (Teacher type only)
              </Label>
              <Combobox
                value={form.teacher_id}
                onChange={(value) => setForm({ ...form, teacher_id: value })}
                options={teacherOptions}
                placeholder="Select teacher"
                testid="tt-form-teacher"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Start Time</Label>
              <Input
                type="time"
                value={form.start_time || ""}
                onChange={(e) =>
                  setForm({ ...form, start_time: e.target.value })
                }
                className="h-9"
                data-testid="tt-form-start"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">End Time</Label>
              <Input
                type="time"
                value={form.end_time || ""}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="h-9"
                data-testid="tt-form-end"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveRow}
              disabled={saving}
              className="erp-primary"
              data-testid="tt-form-save"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Delete timetable period?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              This will remove selected period from class-section timetable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeRow}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* Classes page with sections multi-select per requirement */
function ClassesPage() {
  const permissions = useRouteModulePermissions("academic", "classes");
  const [rows, setRows] = useState([]);
  const [sections, setSections] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const [c, s, st] = await Promise.all([
      api.get("/classes"),
      api.get("/sections"),
      api.get("/staff", { params: { limit: 2000 } }),
    ]);
    setRows(c.data.items || []);
    setSections(s.data.items || []);
    setStaff(st.data.items || []);
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ sections: [], class_teacher_id: "" });
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...row,
      sections: row.sections || [],
      class_teacher_id: row.class_teacher_id
        ? String(row.class_teacher_id)
        : "",
    });
    setOpen(true);
  };

  const teacherRows = staff.filter((member) => {
    const role = String(member.role || "").toLowerCase();
    const teacherType = String(member.teacher_type || "").toLowerCase();
    return (
      role === "teacher" || role === "staff" || teacherType.includes("teach")
    );
  });

  const teacherName = (id) => {
    if (!id) return "-";
    const t = staff.find((s) => String(s.id) === String(id));
    if (!t) return "-";
    return (
      `${t.first_name || ""} ${t.last_name || ""}`.trim() ||
      t.staff_id ||
      `#${t.id}`
    );
  };

  const save = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for classes");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for classes");
      return;
    }
    if (!form.name) return toast.error("Name required");

    const payload = {
      ...form,
      class_teacher_id:
        form.class_teacher_id === "" || form.class_teacher_id == null
          ? null
          : Number(form.class_teacher_id),
    };

    if (editing) await api.put(`/classes/${editing.id}`, payload);
    else await api.post(`/classes`, payload);
    toast.success("Saved");
    setOpen(false);
    load();
  };
  const openDelete = (row) => setDeleteRow(row);
  const del = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for classes");
      return;
    }
    await api.delete(`/classes/${deleteRow.id}`);
    toast.success("Deleted");
    load();
    setDeleteRow(null);
  };

  const sectionNames = [...new Set(sections.map((s) => s.name))].sort();

  return (
    <div data-testid="classes-page">
      <PageTitle
        title="Classes"
        subtitle="Class with bound sections (e.g. Grade 1 → A, B)"
        actions={
          permissions.canAdd ? (
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              data-testid="add-class"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Class
            </Button>
          ) : null
        }
      />
      <DataTable
        testid="classes-table"
        columns={[
          { key: "name", label: "Class Name" },
          { key: "short_name", label: "Short Name" },
          {
            key: "class_teacher_id",
            label: "Class Teacher",
            render: (r) => teacherName(r.class_teacher_id),
          },
          {
            key: "sections",
            label: "Sections",
            render: (r) =>
              (r.sections || []).map((s) => (
                <span
                  key={s}
                  className="inline-block mr-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-semibold border border-indigo-200"
                >
                  {s}
                </span>
              )),
          },
        ]}
        rows={rows}
        onEdit={permissions.canEdit ? openEdit : undefined}
        onDelete={permissions.canDelete ? openDelete : undefined}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Add"} Class</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Class Name *</Label>
              <Input
                value={form.name || ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-9"
                data-testid="cls-name"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Short Name *</Label>
              <Input
                value={form.short_name || ""}
                onChange={(e) =>
                  setForm({ ...form, short_name: e.target.value })
                }
                className="h-9"
                data-testid="cls-short"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Class Teacher</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.class_teacher_id || ""}
                onChange={(e) =>
                  setForm({ ...form, class_teacher_id: e.target.value })
                }
                data-testid="cls-class-teacher"
              >
                <option value="">None</option>
                {teacherRows.map((t) => (
                  <option key={t.id} value={t.id}>
                    {`${t.first_name || ""} ${t.last_name || ""}`.trim() ||
                      t.staff_id ||
                      `#${t.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">
                Sections (multi-select)
              </Label>
              <MultiCheck
                value={form.sections || []}
                onChange={(v) => setForm({ ...form, sections: v })}
                options={sectionNames}
                testid="cls-sections"
                columns={5}
              />
              <div className="text-[10px] text-slate-500 mt-1">
                Add new section labels in Sections master if needed.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="erp-primary"
              data-testid="cls-save"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Delete class?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              This will remove the selected class and its configuration mapping.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SectionsPage() {
  const permissions = useRouteModulePermissions("academic", "sections");
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const [sectionResp, classResp] = await Promise.all([
      api.get("/sections"),
      api.get("/classes"),
    ]);
    setRows(sectionResp.data.items || []);
    setClasses(classResp.data.items || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({});
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...row });
    setOpen(true);
  };

  const save = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for sections");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for sections");
      return;
    }
    if (!form.name) return toast.error("Section name required");
    if (!form.class_id) return toast.error("Class required");

    if (editing) await api.put(`/sections/${editing.id}`, form);
    else await api.post(`/sections`, form);

    toast.success("Saved");
    setOpen(false);
    load();
  };

  const openDelete = (row) => setDeleteRow(row);

  const del = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for sections");
      return;
    }
    await api.delete(`/sections/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  const className = (id) =>
    classes.find((c) => String(c.id) === String(id))?.name || "-";

  return (
    <div data-testid="sections-page">
      <PageTitle
        title="Sections"
        subtitle="Section master"
        actions={
          permissions.canAdd ? (
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              data-testid="add-section"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Section
            </Button>
          ) : null
        }
      />

      <DataTable
        testid="sections-table"
        columns={[
          { key: "name", label: "Section" },
          {
            key: "class_id",
            label: "Class",
            render: (r) => className(r.class_id),
          },
        ]}
        rows={rows}
        onEdit={permissions.canEdit ? openEdit : undefined}
        onDelete={permissions.canDelete ? openDelete : undefined}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Add"} Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Section Name *</Label>
              <Input
                value={form.name || ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-9"
                data-testid="sec-name"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Class *</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.class_id || ""}
                onChange={(e) => setForm({ ...form, class_id: e.target.value })}
                data-testid="sec-class"
              >
                <option value="">Select class</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="erp-primary"
              data-testid="sec-save"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Delete section?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              This will remove selected section mapping.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* Subject Group with class + sections (from class) + subjects multi-select */
function SubjectGroupsPage() {
  const permissions = useRouteModulePermissions("academic", "subject_groups");
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const [g, c, s] = await Promise.all([
      api.get("/subject-groups"),
      api.get("/classes"),
      api.get("/subjects"),
    ]);
    setRows(g.data.items || []);
    setClasses(c.data.items || []);
    setSubjects(s.data.items || []);
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ sections: [], subjects: [] });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, sections: r.sections || [], subjects: r.subjects || [] });
    setOpen(true);
  };
  const save = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for subject groups");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for subject groups");
      return;
    }
    if (!form.name || !form.class_id)
      return toast.error("Name and Class required");
    if (!form.sections || form.sections.length === 0)
      return toast.error("Select at least one section");
    if (!form.subjects || form.subjects.length === 0)
      return toast.error("Select at least one subject");
    const duplicate = rows.find(
      (r) =>
        String(r.name || "")
          .trim()
          .toLowerCase() ===
          String(form.name || "")
            .trim()
            .toLowerCase() &&
        String(r.class_id) === String(form.class_id) &&
        (!editing || r.id !== editing.id),
    );
    if (duplicate)
      return toast.error(
        `Subject group "${form.name}" already exists for this class`,
      );
    if (editing) await api.put(`/subject-groups/${editing.id}`, form);
    else await api.post(`/subject-groups`, form);
    toast.success("Saved");
    setOpen(false);
    load();
  };
  const openDelete = (row) => setDeleteRow(row);
  const del = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for subject groups");
      return;
    }
    await api.delete(`/subject-groups/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  const cls = classes.find((c) => c.id === form.class_id);
  const clsSections = cls?.sections || [];
  const classSectionOptions = clsSections.map((sectionName) => ({
    value: sectionName,
    label: sectionName,
  }));
  const subjectOptions = subjects.map((s) => ({
    value: String(s.id),
    label: `${s.name} (${s.code || s.type})`,
  }));

  return (
    <div data-testid="subj-groups-page">
      <PageTitle
        title="Subject Groups"
        subtitle="Bundle of subjects for class + sections (e.g. 11TH COMMERCE → A, B → 5 subjects)"
        actions={
          permissions.canAdd ? (
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              data-testid="add-sg"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Group
            </Button>
          ) : null
        }
      />
      <DataTable
        testid="sg-table"
        columns={[
          { key: "name", label: "Name" },
          {
            key: "class_id",
            label: "Class",
            render: (r) =>
              classes.find((c) => c.id === r.class_id)?.name || "—",
          },
          {
            key: "sections",
            label: "Sections",
            render: (r) => (r.sections || []).join(", ") || "—",
          },
          {
            key: "subjects",
            label: "Subjects",
            render: (r) => (
              <span className="text-xs">
                {(r.subjects || [])
                  .map((sid) => subjects.find((s) => s.id === sid)?.name)
                  .filter(Boolean)
                  .join(", ") || "—"}
              </span>
            ),
          },
          { key: "description", label: "Description" },
        ]}
        rows={rows}
        onEdit={permissions.canEdit ? openEdit : undefined}
        onDelete={permissions.canDelete ? openDelete : undefined}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Add"} Subject Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Group Name *</Label>
                <Input
                  value={form.name || ""}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-9"
                  data-testid="sg-name"
                  placeholder="e.g. 11TH COMMERCE"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Class *</Label>
                <select
                  className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                  value={form.class_id || ""}
                  onChange={(e) =>
                    setForm({ ...form, class_id: e.target.value, sections: [] })
                  }
                  data-testid="sg-class"
                >
                  <option value="">—</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">
                Sections * (from selected class)
              </Label>
              {clsSections.length === 0 ? (
                <div className="text-xs text-slate-400 border border-dashed rounded p-3">
                  Select a class first
                </div>
              ) : (
                <MultiCheck
                  value={form.sections || []}
                  onChange={(v) => setForm({ ...form, sections: v })}
                  options={classSectionOptions}
                  testid="sg-sections"
                  columns={5}
                />
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold">Subjects *</Label>
              <MultiCheck
                value={(form.subjects || []).map((value) => String(value))}
                onChange={(v) =>
                  setForm({
                    ...form,
                    subjects: v.map((value) => String(value)),
                  })
                }
                options={subjectOptions}
                testid="sg-subjects"
                columns={2}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Description</Label>
              <textarea
                rows={2}
                value={form.description || ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="erp-primary"
              data-testid="sg-save"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Delete subject group?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              This will remove the selected subject group mapping from Academic
              setup.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Academic() {
  const classesPermissions = useRouteModulePermissions("academic", "classes");
  const sectionsPermissions = useRouteModulePermissions("academic", "sections");
  const subjectsPermissions = useRouteModulePermissions("academic", "subjects");
  const subjectGroupsPermissions = useRouteModulePermissions(
    "academic",
    "subject_groups",
  );
  const periodMasterPermissions = useRouteModulePermissions(
    "academic",
    "period_master",
  );
  const timetablePermissions = useRouteModulePermissions(
    "academic",
    "timetables",
  );
  const housesPermissions = useRouteModulePermissions("academic", "houses");
  const clubsPermissions = useRouteModulePermissions("academic", "clubs");
  const categoriesPermissions = useRouteModulePermissions(
    "academic",
    "categories",
  );
  const items = [
    classesPermissions.canView && { to: "/academic/classes", label: "Classes" },
    sectionsPermissions.canView && {
      to: "/academic/sections",
      label: "Sections",
    },
    subjectsPermissions.canView && {
      to: "/academic/subjects",
      label: "Subjects",
    },
    subjectGroupsPermissions.canView && {
      to: "/academic/subject-groups",
      label: "Subject Groups",
    },
    periodMasterPermissions.canView && {
      to: "/academic/period-master",
      label: "Period Master",
    },
    timetablePermissions.canView && {
      to: "/academic/timetable",
      label: "Timetable",
    },
    housesPermissions.canView && { to: "/academic/houses", label: "Houses" },
    clubsPermissions.canView && { to: "/academic/clubs", label: "Clubs" },
    categoriesPermissions.canView && {
      to: "/academic/categories",
      label: "Categories",
    },
  ].filter(Boolean);
  if (items.length === 0) {
    return (
      <div data-testid="academic-page">
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
          <div className="text-base font-semibold text-slate-700">
            No academic workflow assigned
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Ask super admin to enable Academic sub-features for this role.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="academic-page">
      <SubNav items={items} />
      <Routes>
        <Route index element={<Navigate to={items[0].to} replace />} />
        {classesPermissions.canView && (
          <Route path="classes" element={<ClassesPage />} />
        )}
        {sectionsPermissions.canView && (
          <Route path="sections" element={<SectionsPage />} />
        )}
        {subjectsPermissions.canView && (
          <Route
            path="subjects"
            element={
              <MasterCrudPage
                title="Subjects"
                endpoint="/subjects"
                testid="subjects"
                moduleKey="academic"
                submoduleKey="subjects"
                fields={[
                  { key: "name", label: "Subject Name *", required: true },
                  { key: "code", label: "Code (e.g. 041)" },
                  {
                    key: "type",
                    label: "Type",
                    type: "select",
                    options: [
                      { value: "Theory", label: "Theory" },
                      { value: "Practical", label: "Practical" },
                    ],
                  },
                ]}
              />
            }
          />
        )}
        {subjectGroupsPermissions.canView && (
          <Route path="subject-groups" element={<SubjectGroupsPage />} />
        )}
        {periodMasterPermissions.canView && (
          <Route
            path="period-master"
            element={
              <MasterCrudPage
                title="Period Master"
                endpoint="/period-master"
                testid="period-master"
                moduleKey="academic"
                submoduleKey="period_master"
                fields={[
                  { key: "name", label: "Period Name *", required: true },
                  {
                    key: "period_no",
                    label: "Period No *",
                    type: "number",
                    required: true,
                  },
                  {
                    key: "sort_order",
                    label: "Sort Order *",
                    type: "number",
                    required: true,
                  },
                  { key: "start_time", label: "Start Time", type: "time" },
                  { key: "end_time", label: "End Time", type: "time" },
                  {
                    key: "status",
                    label: "Status",
                    type: "select",
                    options: [
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ],
                  },
                ]}
                columns={[
                  { key: "name", label: "Period Name" },
                  { key: "period_no", label: "Period No" },
                  { key: "sort_order", label: "Sort Order" },
                  { key: "start_time", label: "Start" },
                  { key: "end_time", label: "End" },
                  { key: "status", label: "Status" },
                ]}
                scoped
              />
            }
          />
        )}
        {timetablePermissions.canView && (
          <Route path="timetable" element={<TimetablePlannerPage />} />
        )}
        {housesPermissions.canView && (
          <Route
            path="houses"
            element={
              <MasterCrudPage
                title="Houses"
                endpoint="/houses"
                testid="houses"
                moduleKey="academic"
                submoduleKey="houses"
                fields={[
                  { key: "name", label: "Name *", required: true },
                  { key: "color", label: "Color" },
                ]}
              />
            }
          />
        )}
        {clubsPermissions.canView && (
          <Route
            path="clubs"
            element={
              <MasterCrudPage
                title="Clubs"
                endpoint="/clubs"
                testid="clubs"
                moduleKey="academic"
                submoduleKey="clubs"
                fields={[{ key: "name", label: "Name *", required: true }]}
              />
            }
          />
        )}
        {categoriesPermissions.canView && (
          <Route
            path="categories"
            element={
              <MasterCrudPage
                title="Student Categories"
                endpoint="/student-categories"
                testid="categories"
                moduleKey="academic"
                submoduleKey="categories"
                fields={[{ key: "name", label: "Name *", required: true }]}
              />
            }
          />
        )}
      </Routes>
    </div>
  );
}
